import React from "react";
import styles from "./WaysToStyleCarousel.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  StylingLookProductFromServer,
  StylingLook,
  StylingLookWithStockPrice,
} from "../../types/productTypes/stylingLookProduct";
import { ProductData } from "../../types/productTypes/product";
import { getPrimaryImage, getHeroProduct } from "../../utils/product";
import Image from "../image/Image";
import PlusPill from "../pills/plusPill/PlusPill";
import LookLabel from "./LookLabel";
import { getLookHeroImageUrl } from "../../utils/heroProductUtils";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import { WAYS_TO_STYLE_OPEN_MODAL_EVENT } from "../../constants";
import getClientStockPrice from "../../api/clientStockPriceApi";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import { mergeStylingLooksWithStockPrice } from "./utils";
import classnamesBind from "classnames/bind";

const classnames = classnamesBind.bind(styles);

interface WaysToStyleCarouselProps {
  stylingLooks: StylingLook[];
}

const renderStylingLookHeroImage = (look: StylingLook, index: number) => {
  const heroProduct = getHeroProduct(look.products)[0] ?? look.products[0];

  if (!heroProduct) {
    return null;
  }
  return (
    <Image
      key={`styling-look-hero-${index}`}
      url={getLookHeroImageUrl({
        product: heroProduct,
        looksProducts: look.products,
      })}
      alternateText={heroProduct?.name}
      type="carousel"
      width={640}
      additionalClasses=""
      loadingType="lazy"
      ariaHidden={false}
    />
  );
};

const renderStylingLookThumbnailImage = (
  product: StylingLookProductFromServer,
  index: number
) => (
  <Image
    key={`styling-look-thumbnail-${index}`}
    url={getPrimaryImage(product.images)?.url}
    alternateText={product.name}
    type="thumbnail"
    width={75}
    additionalClasses=""
    loadingType="lazy"
    ariaHidden={false}
  />
);

const getThumbnailsToShow = (
  products: StylingLookProductFromServer[],
  currentProductId?: number
) => {
  const orderedProducts = [...products];

  if (currentProductId !== undefined) {
    const currentIndex = orderedProducts.findIndex(
      (product) => product.id === currentProductId
    );

    if (currentIndex > 0) {
      const [currentProduct] = orderedProducts.splice(currentIndex, 1);
      orderedProducts.unshift(currentProduct);
    }
  }
  return {
    thumbnailsToShow: orderedProducts.slice(0, 4),
    shouldShowPlusPill: orderedProducts.length > 4,
    remainingCount: Math.max(0, orderedProducts.length - 4),
  };
};

const getStockPriceForLooks = async (
  stylingLooks: StylingLook[],
  storeCode: string,
  index: number
): Promise<StylingLookWithStockPrice | undefined> => {
  // Get the 3 looks to fetch: current, previous, and next
  const prevIndex = Math.max(0, index - 1);
  const nextIndex = Math.min(stylingLooks.length - 1, index + 1);

  const looksToFetch = [
    stylingLooks[prevIndex],
    stylingLooks[index],
    stylingLooks[nextIndex],
  ];

  const productMap = new Map(
    looksToFetch.flatMap((look) => look.products).map((p) => [p.id, p])
  );
  const uniqueProducts = Array.from(productMap.values());

  try {
    const stockPriceData = await getClientStockPrice({
      products: uniqueProducts,
      storeCode,
    });

    if (stockPriceData?.length) {
      const updatedLooks = mergeStylingLooksWithStockPrice(
        looksToFetch,
        stockPriceData
      );

      const clickedLookIndex = prevIndex === index ? 0 : 1;
      return updatedLooks[clickedLookIndex];
    }
  } catch (error) {
    logError(ERROR_TYPE.StockPriceError, {
      action: "getClientStockPrice",
      caller: "WaysToStyleCarousel_handleClick",
      apiName: "stockPriceApi",
      message: (error as Error).message,
    });
  }
};

export default function WaysToStyleCarousel({
  stylingLooks,
}: WaysToStyleCarouselProps) {
  const { getTranslation, storeCode, product } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    storeCode: string;
    product: ProductData;
  };

  if (!stylingLooks?.length) {
    return null;
  }

  const isSingleLook = stylingLooks.length === 1;

  const handleClick =
    (index: number) => async (event: React.MouseEvent<HTMLButtonElement>) => {
      const lookWithStockPrice = await getStockPriceForLooks(
        stylingLooks,
        storeCode,
        index
      );

      handleModalTriggerEvent({
        focusOnCloseElement: event.currentTarget,
        eventKey: WAYS_TO_STYLE_OPEN_MODAL_EVENT,
        data: {
          selectedLook: lookWithStockPrice ?? stylingLooks[index],
          index,
        },
      });
    };

  return (
    <div
      data-testid="waysToStyleCarousel"
      className={styles.carousel}
      aria-label={getTranslation("pdp_buy_the_look_carousel_accessibility")}
    >
      {stylingLooks.map((look, index) => {
        const { thumbnailsToShow, shouldShowPlusPill, remainingCount } =
          getThumbnailsToShow(look.products, product.id);

        return (
          <button
            onClick={handleClick(index)}
            key={`${look.products[0]?.id}-${index}`}
            className={classnames(styles.carouselItem, {
              singleLook: isSingleLook,
              multipleLooks: !isSingleLook,
            })}
          >
            <div className={styles.heroImage}>
              {renderStylingLookHeroImage(look, index)}
              <LookLabel look={look} />
            </div>
            <div className={styles.thumbnailContainer}>
              {thumbnailsToShow.map((product, index) => (
                <div
                  key={`${product?.id}-${index}`}
                  className={styles.thumbnail}
                >
                  {renderStylingLookThumbnailImage(product, index)}
                </div>
              ))}
              {shouldShowPlusPill && (
                <PlusPill
                  number={remainingCount.toString()}
                  additionalClasses={styles.lightTheme}
                />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
